import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Form, Alert, Toast } from 'react-bootstrap';
import { useAppDispatch } from '../redux/store';
import { useStages } from '../redux/hooks/useStages';
import { useCandidates } from '../redux/hooks/useCandidates';
import { handleCandidateStageChange as updateStageCount } from '../redux/slices/stagesSlice';
import CandidateList from '../components/candidate/CandidateList';
import { Candidate, Stage } from '../types/types';
import LoadingSpinner from '../components/common/LoadingSpinner';

const StageCandidatesPage: React.FC = () => {
    const { stageId } = useParams<{ stageId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    // Redux hooks for stages
    const {
        activeStagesSorted: stages,
        stagesSorted: allStages,
        getStageById,
        loading: stagesLoading,
        error: stagesError,
        ensureDataLoaded: ensureStagesLoaded
    } = useStages();

    // Redux hooks for candidates
    const {
        candidates: allCandidates,
        recentCandidates,
        getCandidatesForStage,
        changeStage,
        loading: candidatesLoading,
        error: candidatesError,
        fetchAll: fetchAllCandidates,
        fetchRecent: fetchRecentCandidates,
        fetchByStage: fetchCandidatesByStage,
        ensureCandidatesLoaded,
        ensureRecentCandidatesLoaded,
        ensureCandidatesForStageLoaded
    } = useCandidates();

    // Local state
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortBy, setSortBy] = useState<string>('lastName');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toastMessage, setToastMessage] = useState<string>('');
    const [toastVariant, setToastVariant] = useState<'success' | 'danger'>('success');
    const [localError, setLocalError] = useState<string | null>(null);

    // Page type detection
    const isRecentPage = location.pathname === '/candidates/recent';
    const isAllCandidatesPage = location.pathname === '/candidates';

    // Get current stage and base candidates for the page
    const currentStage = stageId ? getStageById(stageId) : null;
    const baseCandidates = isRecentPage
        ? recentCandidates
        : isAllCandidatesPage
            ? allCandidates
            : stageId
                ? getCandidatesForStage(stageId)
                : [];

    // Fetch data on mount and when dependencies change
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Always ensure stages are loaded
                await ensureStagesLoaded();

                // Fetch candidates based on page type
                if (isRecentPage) {
                    await ensureRecentCandidatesLoaded();
                } else if (isAllCandidatesPage) {
                    await ensureCandidatesLoaded();
                } else if (stageId) {
                    await ensureCandidatesForStageLoaded(stageId);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setLocalError('Failed to load data. Please try again.');
            }
        };

        fetchData();
    }, [
        stageId,
        isRecentPage,
        isAllCandidatesPage,
        ensureStagesLoaded,
        ensureCandidatesLoaded,
        ensureRecentCandidatesLoaded,
        ensureCandidatesForStageLoaded
    ]);

    const handleStageChange = async (candidateId: string, newStageId: string, notes?: string) => {
        try {
            // Get the candidate's current stage before the change
            const candidate = baseCandidates.find(c => c.id === candidateId);
            const oldStageId = candidate?.stageId;

            // Call the Redux action to change the candidate's stage
            await changeStage(candidateId, {
                candidateId,
                newStageId,
                notes
            }).unwrap();

            // Update Redux stage counts
            if (oldStageId && oldStageId !== newStageId) {
                dispatch(updateStageCount({
                    fromStageId: oldStageId,
                    toStageId: newStageId
                }));
            }

            const targetStage = stages.find(s => s.id === newStageId);
            setToastVariant('success');

            if (stageId !== newStageId) {
                setToastMessage(`Candidate moved to ${targetStage?.name || 'new stage'}`);
            } else {
                setToastMessage('Candidate stage updated successfully');
            }

            setShowToast(true);
        } catch (err: any) {
            console.error('Error changing candidate stage:', err);
            setToastVariant('danger');
            setToastMessage(err.message || 'Failed to update candidate stage. Please try again.');
            setShowToast(true);
        }
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSortBy(e.target.value);
    };

    const toggleSortDirection = () => {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const handleStageFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedStageId = e.target.value;
        if (selectedStageId) {
            navigate(`/candidates/stage/${selectedStageId}`);
        } else {
            navigate('/candidates');
        }
    };

    const handleRefresh = async () => {
        try {
            if (isRecentPage) {
                await fetchRecentCandidates();
            } else if (isAllCandidatesPage) {
                await fetchAllCandidates();
            } else if (stageId) {
                await fetchCandidatesByStage(stageId);
            }
            setLocalError(null);
        } catch (error) {
            setLocalError('Failed to refresh data. Please try again.');
        }
    };

    const getPageTitle = () => {
        if (isRecentPage) return 'Recent Candidates';
        if (isAllCandidatesPage) return 'All Candidates';
        return currentStage?.name || 'Candidates';
    };

    const getPageDescription = () => {
        if (isRecentPage) return 'Candidates added in the last 7 days';
        if (isAllCandidatesPage) return 'All candidates across all stages';
        return currentStage?.description;
    };

    // Apply filtering and sorting to base candidates
    const filteredCandidates = baseCandidates
        .filter(candidate => {
            if (!searchTerm) return true;

            const fullName = `${candidate.firstName} ${candidate.lastName}`.toLowerCase();
            const email = candidate.email.toLowerCase();
            const search = searchTerm.toLowerCase();

            return fullName.includes(search) || email.includes(search);
        })
        .sort((a, b) => {
            let comparison = 0;

            switch (sortBy) {
                case 'firstName':
                    comparison = a.firstName.localeCompare(b.firstName);
                    break;
                case 'lastName':
                    comparison = a.lastName.localeCompare(b.lastName);
                    break;
                case 'email':
                    comparison = a.email.localeCompare(b.email);
                    break;
                case 'daysInStage':
                    comparison = a.daysInCurrentStage - b.daysInCurrentStage;
                    break;
                case 'createdAt':
                    comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                    break;
                default:
                    comparison = a.lastName.localeCompare(b.lastName);
            }

            return sortDirection === 'asc' ? comparison : -comparison;
        });

    const getCandidateCountDisplay = () => {
        if (isRecentPage) return recentCandidates.length;
        if (isAllCandidatesPage) return allCandidates.length;
        return currentStage?.candidateCount || 0;
    };

    const isLoading = stagesLoading || candidatesLoading;
    const hasError = stagesError || candidatesError || localError;

    return (
        <>
            <Container fluid>
                {/* Toast Notification */}
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={3000}
                    autohide
                    style={{
                        position: 'fixed',
                        top: 20,
                        right: 20,
                        zIndex: 9999
                    }}
                    bg={toastVariant}
                >
                    <Toast.Header closeButton>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : 'Error'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toastVariant === 'success' ? 'text-white' : ''}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>

                {/* Page Header */}
                <Row className="mb-4 align-items-center">
                    <Col>
                        <div className="d-flex align-items-center">
                            <h1 className="mb-0">
                                {getPageTitle()}
                            </h1>
                            {isLoading && (
                                <LoadingSpinner centered/>
                            )}
                        </div>
                        {getPageDescription() && (
                            <p className="text-muted mb-0">{getPageDescription()}</p>
                        )}
                    </Col>
                    <Col md="auto">
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={isLoading}
                            >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Refresh
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => navigate('/candidates/new')}
                            >
                                <i className="bi bi-person-plus me-1"></i> Add Candidate
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* Error Display */}
                {hasError && (
                    <Alert
                        variant="warning"
                        className="mb-4"
                        dismissible
                        onClose={() => setLocalError(null)}
                    >
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {stagesError && `Stages: ${stagesError}. `}
                        {candidatesError && `Candidates: ${candidatesError}. `}
                        {localError && localError}
                    </Alert>
                )}

                {/* Filters and Controls */}
                <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-white">
                        <Row className="align-items-center">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small text-muted">Filter by Stage</Form.Label>
                                    <Form.Select
                                        value={stageId || ''}
                                        onChange={handleStageFilterChange}
                                        disabled={isLoading}
                                        size="sm"
                                    >
                                        <option value="">All Stages</option>
                                        <option value="recent">Recent (Last 7 days)</option>
                                        {stages.map(stage => (
                                            <option key={stage.id} value={stage.id}>
                                                {stage.name} ({stage.candidateCount || 0})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="small text-muted">Search Candidates</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by name or email"
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        size="sm"
                                        disabled={isLoading}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small text-muted">Sort By</Form.Label>
                                    <Form.Select
                                        value={sortBy}
                                        onChange={handleSortChange}
                                        size="sm"
                                        disabled={isLoading}
                                    >
                                        <option value="lastName">Last Name</option>
                                        <option value="firstName">First Name</option>
                                        <option value="email">Email</option>
                                        <option value="daysInStage">Days in Stage</option>
                                        <option value="createdAt">Date Added</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={2}>
                                <Form.Label className="small text-muted">&nbsp;</Form.Label>
                                <Button
                                    variant="outline-secondary"
                                    className="w-100"
                                    size="sm"
                                    onClick={toggleSortDirection}
                                    disabled={isLoading}
                                >
                                    <i className={`bi bi-sort-${sortDirection === 'asc' ? 'down' : 'up'}`}></i>
                                    {' '}
                                    {sortDirection === 'asc' ? 'Asc' : 'Desc'}
                                </Button>
                            </Col>
                        </Row>
                    </Card.Header>

                    <Card.Body>
                        {isLoading && baseCandidates.length === 0 ? (
                            <LoadingSpinner centered />
                        ) : filteredCandidates.length === 0 ? (
                            <Alert variant="info" className="mb-0">
                                <div className="text-center py-3">
                                    <i className="bi bi-people display-4 text-muted"></i>
                                    <h5 className="mt-2">
                                        {searchTerm
                                            ? "No candidates match your search criteria"
                                            : isRecentPage
                                                ? "No new candidates in the last 7 days"
                                                : baseCandidates.length === 0
                                                    ? "No candidates in this stage yet"
                                                    : "No candidates found"}
                                    </h5>
                                    <p className="text-muted">
                                        {searchTerm ? (
                                            <>
                                                Try adjusting your search terms or{' '}
                                                <Button
                                                    variant="link"
                                                    className="p-0"
                                                    onClick={() => setSearchTerm('')}
                                                >
                                                    clear the search
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                Get started by{' '}
                                                <Button
                                                    variant="link"
                                                    className="p-0"
                                                    onClick={() => navigate('/candidates/new')}
                                                >
                                                    adding a new candidate
                                                </Button>
                                            </>
                                        )}
                                    </p>
                                </div>
                            </Alert>
                        ) : (
                            <CandidateList
                                candidates={filteredCandidates}
                                onStageChange={handleStageChange}
                                stages={allStages}
                            />
                        )}
                    </Card.Body>

                    {/* Footer with stats */}
                    <Card.Footer className="text-muted bg-light">
                        <Row className="align-items-center">
                            <Col>
                                <small>
                                    Showing <strong>{filteredCandidates.length}</strong> of{' '}
                                    <strong>{baseCandidates.length}</strong> candidates
                                    {searchTerm && (
                                        <>
                                            {' '}matching "<strong>{searchTerm}</strong>"
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 ms-2"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x-circle"></i> Clear
                                            </Button>
                                        </>
                                    )}
                                </small>
                            </Col>
                            <Col md="auto">
                                <div className="d-flex align-items-center gap-3">
                                    {currentStage && (
                                        <Badge bg="info" className="fs-6">
                                            <i className="bi bi-clock me-1"></i>
                                            Avg. days: {currentStage.averageDaysInStage || 'N/A'}
                                        </Badge>
                                    )}
                                    {isRecentPage && (
                                        <Badge bg="success" className="fs-6">
                                            <i className="bi bi-calendar-week me-1"></i>
                                            Last 7 days
                                        </Badge>
                                    )}
                                    <Badge bg="secondary" className="fs-6">
                                        <i className="bi bi-people me-1"></i>
                                        Total: {getCandidateCountDisplay()}
                                    </Badge>
                                </div>
                            </Col>
                        </Row>
                    </Card.Footer>
                </Card>
            </Container>
        </>
    );
};

export default StageCandidatesPage;